import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, ImageIcon, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const ChatDetail = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token) {
      navigate('/auth');
      return;
    }
    setCurrentUserId(user.id);
    fetchConversation();
    connectWebSocket(token);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    // Small delay to ensure DOM is updated before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const connectWebSocket = (token) => {
    if (!WS_URL) return;
    
    const ws = new WebSocket(`${WS_URL}/api/ws/${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message' && data.message.conversation_id === conversationId) {
        setMessages(prev => [...prev, data.message]);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    wsRef.current = ws;
  };

  const fetchConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversation(res.data);
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      navigate('/chats');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation) return;

    setSending(true);
    const messageContent = newMessage;
    setNewMessage('');

    try {
      // Determine receiver
      const receiverId = currentUserId === conversation.buyer_id 
        ? conversation.seller_id 
        : conversation.buyer_id;

      // Send via WebSocket if connected
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'send_message',
          listing_id: conversation.listing_id,
          receiver_id: receiverId,
          content: messageContent
        }));
      } else {
        // Fallback to REST API
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/messages`, {
          listing_id: conversation.listing_id,
          content: messageContent
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(prev => [...prev, res.data]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent); // Restore message if failed
    } finally {
      setSending(false);
    }
  };

  const getOtherUser = () => {
    if (!conversation) return null;
    return currentUserId === conversation.buyer_id 
      ? conversation.seller 
      : conversation.buyer;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const otherUser = getOtherUser();

  return (
    <div className="fixed inset-0 bg-[#F8F9FA] flex flex-col pb-16 md:pb-0" data-testid="chat-detail">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/chats')} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6 text-gray-900" />
          </button>
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            {otherUser?.avatar_url ? (
              <img src={otherUser.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 truncate">{otherUser?.full_name || 'User'}</h1>
            {conversation?.listing && (
              <p className="text-xs text-gray-500 truncate">{conversation.listing.title}</p>
            )}
          </div>
        </div>
      </div>

      {/* Listing Preview - Fixed */}
      {conversation?.listing && (
        <div 
          onClick={() => navigate(`/listing/${conversation.listing.id}`)}
          className="flex-shrink-0 bg-white border-b border-gray-100 cursor-pointer hover:bg-gray-50"
        >
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {conversation.listing.image_url ? (
                <img src={conversation.listing.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{conversation.listing.title}</p>
              <p className="text-sm font-bold text-[#E31C25]">AED {conversation.listing.price?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Messages - Scrollable area that takes remaining space */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const isOwn = message.sender_id === currentUserId;
              return (
                <div
                  key={message.id || index}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                      isOwn
                        ? 'bg-[#E31C25] text-white rounded-br-sm'
                        : 'bg-white border border-gray-100 text-gray-900 rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                    <p className={`text-xs mt-1 ${isOwn ? 'text-red-200' : 'text-gray-400'}`}>
                      {formatTime(message.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input - Fixed at bottom */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 p-3">
        <form onSubmit={sendMessage} className="max-w-2xl mx-auto flex items-end gap-3">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:border-[#E31C25] resize-none max-h-32 overflow-y-auto"
            style={{ minHeight: '48px' }}
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 bg-[#E31C25] text-white rounded-full flex items-center justify-center disabled:bg-gray-300 transition-colors flex-shrink-0"
            data-testid="send-message-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatDetail;
